import type { SxProps, Theme } from '@mui/material/styles';

/**
 * Shared fills for “grade / score” pills (below vs meets threshold).
 * Keep in sync anywhere we use these backgrounds on small, bold text.
 */
export const GRADE_BADGE_BG_BELOW_THRESHOLD = '#FFD4DC';
export const GRADE_BADGE_BG_MEETS_THRESHOLD = '#D0F0C0';
export const GRADE_BADGE_BG_NEUTRAL = '#d0d0d0';

/**
 * Foreground for text on the badge backgrounds above.
 * #1a1a1a exceeds WCAG AA 4.5:1 on #FFD4DC and #D0F0C0 for caption-sized bold text.
 * (Theme `caption` uses `text.secondary` / #666666 unless overridden.)
 */
export const GRADE_BADGE_FOREGROUND = '#1a1a1a';

/**
 * Same hex as {@link GRADE_BADGE_FOREGROUND}; use where `text.secondary` fails WCAG on
 * `palette.action.hover` (~#DBDCDD), e.g. `ListItemText` secondary in `MenuItem` rows.
 */
export const ACCESSIBLE_SUBTEXT_ON_INTERACTIVE = GRADE_BADGE_FOREGROUND;

/**
 * `Typography variant="caption"` uses theme `text.secondary` (#666). On tinted badge fills that
 * fails WCAG; `RouterLink` + `color: 'inherit'` can also force #666 on descendants. `&&` raises
 * specificity so {@link GRADE_BADGE_FOREGROUND} wins.
 */
export const GRADE_BADGE_CAPTION_ON_TINT_SX: SxProps<Theme> = {
  fontWeight: 'bold',
  textTransform: 'uppercase',
  '&&': {
    color: GRADE_BADGE_FOREGROUND,
  },
};

/** Full pill `sx` for caption-grade badges (neutral or threshold background). */
export function gradeBadgeCaptionPillSx(backgroundColor: string): SxProps<Theme> {
  return {
    ...GRADE_BADGE_CAPTION_ON_TINT_SX,
    borderRadius: '10px',
    pl: 1,
    pr: 1,
    pt: 0.5,
    pb: 0.5,
    backgroundColor,
  };
}
